import { clearSession, loadSession } from './tokenStore'
import { isRefreshAuthError, refreshTokens } from './cognitoAuth'
import { uiBuild, uiEnv } from './uiMeta'

const REFRESH_SKEW_MS = 60 * 1000

let refreshPromise = null
let onSessionExpired = null

export function setSessionExpiredHandler(handler) {
  onSessionExpired = handler
}

export function notifySessionExpired() {
  clearSession()
  if (typeof onSessionExpired === 'function') {
    onSessionExpired()
  }
}

export async function getValidAccessToken() {
  const session = loadSession()
  if (!session?.accessToken && !session?.refreshToken) {
    return null
  }
  const expiringSoon =
    !session.accessToken || !session.expiresAt || session.expiresAt - Date.now() < REFRESH_SKEW_MS
  if (session.accessToken && !expiringSoon) {
    return session.accessToken
  }
  if (!session.refreshToken) {
    notifySessionExpired()
    return null
  }
  if (!refreshPromise) {
    refreshPromise = refreshTokens()
      .then((next) => next.accessToken)
      .catch((error) => {
        if (isRefreshAuthError(error) || error?.message === 'No refresh token') {
          notifySessionExpired()
          return null
        }
        throw error
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

export async function apiFetch(path, options = {}) {
  const apiUrl = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '')
  if (!apiUrl) {
    throw new Error('VITE_API_URL is not configured')
  }

  const headers = {
    'Content-Type': 'application/json',
    'X-Ui-Env': uiEnv(),
    'X-Ui-Build': uiBuild(),
    ...(options.headers || {}),
  }

  if (!options.skipAuth) {
    const token = await getValidAccessToken()
    if (!token) {
      const error = new Error('Not signed in')
      error.status = 401
      throw error
    }
    headers.Authorization = token
  }

  const response = await fetch(`${apiUrl}${path}`, {
    ...options,
    headers,
  })

  if (response.status === 401 && !options.skipAuth && !options._retried) {
    const retriedToken = await getValidAccessToken()
    if (!retriedToken) {
      notifySessionExpired()
      const unauthorized = new Error('Unauthorized')
      unauthorized.status = 401
      throw unauthorized
    }
    return apiFetch(path, { ...options, _retried: true })
  }

  const contentType = response.headers.get('content-type') || ''
  let data
  if (contentType.includes('application/json')) {
    data = await response.json()
  } else {
    data = { message: await response.text() }
  }

  return {
    ok: response.ok,
    status: response.status,
    data,
  }
}
